/**
 * Fetch resiliente para APIs de checkout (Stripe / MP).
 * Safari/IG WebView suele lanzar "Load failed" en el primer intento.
 */

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return true;
  const m = err.message.toLowerCase();
  return (
    m.includes("load failed") ||
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network request failed") ||
    m.includes("aborted") ||
    m.includes("timeout") ||
    err.name === "AbortError"
  );
}

export type FetchCheckoutJsonResult<T> = {
  ok: boolean;
  status: number;
  data: T;
};

export async function fetchCheckoutJson<T>(
  url: string,
  init: RequestInit,
  options?: {
    retries?: number;
    timeoutMs?: number;
    retryDelayMs?: number;
  },
): Promise<FetchCheckoutJsonResult<T>> {
  const retries = options?.retries ?? 3;
  const timeoutMs = options?.timeoutMs ?? 28_000;
  const retryDelayMs = options?.retryDelayMs ?? 450;

  let lastError: unknown;

  for (let attempt = 0; attempt < retries; attempt++) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        ...init,
        signal: controller.signal,
        credentials: "same-origin",
      });
      window.clearTimeout(timer);

      const data = (await res.json().catch(() => ({}))) as T;

      const retryableStatus = res.status === 502 || res.status === 503 || res.status === 504;
      if (retryableStatus && attempt < retries - 1) {
        await sleep(retryDelayMs * (attempt + 1));
        continue;
      }

      return { ok: res.ok, status: res.status, data };
    } catch (err) {
      window.clearTimeout(timer);
      lastError = err;
      if (attempt < retries - 1 && isRetryableNetworkError(err)) {
        await sleep(retryDelayMs * (attempt + 1));
        continue;
      }
      throw err;
    }
  }

  throw lastError ?? new Error("checkout_fetch_failed");
}